import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ShieldCheck, Lock, CreditCard, Check } from 'lucide-react';
export function Hero() {
  const bullets = ['Evaluaciones clínicas organizadas paso a paso', 'Pruebas ortopédicas y casos clínicos reales', 'Útil tanto para práctica clínica como para consultorio', 'Todo estructurado en un solo sistema clínico'];
  return <section className="relative pt-3 pb-8 sm:pt-6 sm:pb-12 md:pt-12 md:pb-20 overflow-hidden bg-navy-900 border-b border-white/5" data-id="element-200">
      {/* Subtle dot pattern */}
      <div className="absolute inset-0 opacity-[0.04] pointer-events-none" data-id="element-201">
        <div className="absolute inset-0" style={{
        backgroundImage: 'radial-gradient(circle, rgba(201,168,76,0.5) 1px, transparent 1px)',
        backgroundSize: '40px 40px'
      }} data-id="element-202" />
      </div>

      {/* Soft gold radial glow */}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[900px] h-[700px] bg-gold/[0.07] blur-[120px] rounded-full pointer-events-none" data-id="element-203"></div>

      {/* Top brand bar */}
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-3 sm:mb-5 md:mb-8" data-id="element-204">
        <div className="flex items-center justify-center sm:justify-between" data-id="element-205">
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} className="flex items-center gap-1.5" data-id="element-206">
            <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-full border border-gold/40 flex items-center justify-center text-gold text-[10px]" data-id="element-207">
              🩺
            </div>
            <span className="font-editorial text-gold text-xs sm:text-base tracking-wide" data-id="element-208">
              Atlas Clínico
            </span>
          </motion.div>
        </div>
      </div>

      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center flex flex-col items-center" data-id="element-209">
        {/* Trust line */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} className="inline-flex items-center gap-1.5 mb-2.5 sm:mb-4" data-id="element-210">
          <div className="flex gap-0.5" data-id="element-211">
            {[...Array(5)].map((_, i) => <Star key={i} size={11} className="fill-gold text-gold sm:w-3 sm:h-3" data-id="element-212" />)}
          </div>
          <span className="text-[11px] sm:text-sm text-gray-300 font-medium" data-id="element-213">
            <span className="text-white font-bold" data-id="element-214">4.9/5</span> · +300
            estudiantes
          </span>
        </motion.div>

        {/* Eyebrow */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.05
      }} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border border-gold/30 bg-gold/5 text-gold text-[9px] sm:text-xs font-bold tracking-[0.12em] uppercase mb-4 sm:mb-6 max-w-[320px] sm:max-w-md leading-tight" data-id="element-215">
          <span className="w-1 h-1 rounded-full bg-gold animate-pulse flex-shrink-0" data-id="element-216"></span>
          <span data-id="element-217">Sistema clínico — Atlas Clínico</span>
        </motion.div>

        {/* EMOTIONAL HEADLINE */}
        <motion.h1 initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.1
      }} className="font-heading font-extrabold text-white leading-[1.05] tracking-tight mb-4 sm:mb-6 max-w-4xl text-[1.65rem] sm:text-4xl md:text-5xl lg:text-[3.75rem]" data-id="element-218">
          ¿Sabés{' '}
          <span className="font-editorial italic text-gold font-medium" data-id="element-219">
            teoría
          </span>
          , pero cuando tenés un paciente adelante{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-light via-gold to-gold-light" data-id="element-220">
            no sabés qué evaluar primero
          </span>
          ?
        </motion.h1>

        {/* PRODUCT MOCKUP — compact, below headline */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.9
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        delay: 0.2,
        duration: 0.6,
        ease: 'easeOut'
      }} className="relative flex justify-center mb-5 sm:mb-7 w-full" data-id="element-221">
          <div className="relative w-full max-w-[280px] sm:max-w-[380px] md:max-w-[460px]" data-id="element-222">
            <div className="absolute inset-0 bg-gold/20 blur-[80px] rounded-full scale-75 pointer-events-none" data-id="element-223"></div>
            <div className="absolute inset-0 bg-navy-500/30 blur-[60px] rounded-full scale-90 pointer-events-none" data-id="element-224"></div>

            <motion.img src="https://cdn.magicpatterns.com/uploads/oGvgXBXVGL78eTMFH1YLiL/Diseno_sin_titulo_(3).png" alt="Mega Pack Kinesiología — Sistema de Evaluación Clínica" width={920} height={920} loading="eager" decoding="async" {...{
            fetchpriority: 'high'
          } as any} className="relative w-full drop-shadow-[0_20px_50px_rgba(0,0,0,0.7)]" animate={{
            y: [0, -6, 0]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-225" />
          </div>
        </motion.div>

        {/* SUBHEADLINE */}
        <motion.p initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.3
      }} className="text-sm sm:text-lg md:text-xl text-gray-200 font-medium mb-5 sm:mb-7 max-w-2xl leading-relaxed px-2" data-id="element-226">
          El <span className="text-white font-semibold" data-id="element-227">sistema clínico</span>{' '}
          que utilizan estudiantes y kinesiólogos para{' '}
          <span className="font-editorial italic text-gold font-normal" data-id="element-228">
            organizar la evaluación
          </span>
          , tomar mejores decisiones y{' '}
          <span className="font-editorial italic text-gold font-normal" data-id="element-229">
            sentirse más seguros
          </span>{' '}
          frente al paciente.
        </motion.p>

        {/* EMOTIONAL BULLETS */}
        <motion.ul initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.4
      }} className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 mb-6 sm:mb-8 max-w-2xl w-full text-left" data-id="element-230">
          {bullets.map((b, idx) => <li key={idx} className="flex items-start gap-2.5 text-[13px] sm:text-[15px] text-white/90 leading-snug" data-id="element-231">
              <span className="mt-0.5 flex-shrink-0 w-4.5 h-4.5 sm:w-5 sm:h-5 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center" data-id="element-232">
                <Check size={11} className="text-gold" strokeWidth={3} data-id="element-233" />
              </span>
              <span data-id="element-234">{b}</span>
            </li>)}
        </motion.ul>

        {/* PRIMARY CTA */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.45
      }} className="flex flex-col items-center w-full mb-4 sm:mb-5" data-id="element-235">
          <a href="#pricing" className="group inline-flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-navy-950 font-bold text-sm sm:text-lg px-6 sm:px-10 py-3.5 sm:py-5 rounded-full transition-all transform hover:scale-[1.02] shadow-[0_0_50px_rgba(201,168,76,0.35)] w-full max-w-[340px] sm:max-w-none sm:w-auto text-center leading-tight" data-id="element-236">
            QUIERO ACCEDER AL MEGA PACK
            <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform flex-shrink-0" data-id="element-237" />
          </a>

          {/* Below-CTA microcopy */}
          <div className="flex flex-wrap justify-center items-center gap-x-2 gap-y-1 mt-3 text-[11px] sm:text-sm text-gray-400" data-id="element-238">
            <span className="inline-flex items-center gap-1" data-id="element-239">
              <span className="w-1.5 h-1.5 rounded-full bg-green-400" data-id="element-240"></span>
              Acceso inmediato
            </span>
            <span className="text-gray-600" data-id="element-241">·</span>
            <span data-id="element-242">Compatible con celular, tablet y PC</span>
          </div>
        </motion.div>

        {/* Trust/payment badges */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 0.55
      }} className="flex flex-wrap justify-center items-center gap-x-3 sm:gap-x-4 gap-y-1.5 text-[10px] sm:text-sm text-gray-400 mt-2" data-id="element-243">
          <div className="flex items-center gap-1" data-id="element-244">
            <ShieldCheck size={12} className="text-green-400 flex-shrink-0 sm:w-3.5 sm:h-3.5" data-id="element-245" />
            <span data-id="element-246">Garantía 7 días</span>
          </div>
          <span className="text-gray-600" data-id="element-247">·</span>
          <div className="flex items-center gap-1" data-id="element-248">
            <Lock size={12} className="text-green-400 flex-shrink-0 sm:w-3.5 sm:h-3.5" data-id="element-249" />
            <span data-id="element-250">Pago seguro</span>
          </div>
          <span className="text-gray-600" data-id="element-251">·</span>
          <div className="flex items-center gap-1" data-id="element-252">
            <CreditCard size={12} className="text-blue-400 flex-shrink-0 sm:w-3.5 sm:h-3.5" data-id="element-253" />
            <span data-id="element-254">Mercado Pago</span>
          </div>
        </motion.div>
      </div>
    </section>;
}